/**
 * Copies bundle/engraving line properties into cart attributes
 * so they appear in Shopify admin Additional details (including the mobile app).
 * Each set or engraved product is a numbered attribute so two sets stay distinct.
 */
(function () {
  const ATTR_PREFIX = 'פריט ';
  const SOURCE = 'bundle-order-attributes';
  const MAX_ATTR_LENGTH = 255;
  const CONTINUATION = ['', 'ב', 'ג', 'ד', 'ה'];

  let syncing = false;
  let queuedCart = null;
  let pending = Promise.resolve();

  function cartJsUrl() {
    const base = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
    return String(base).replace(/\/$/, '') + '.js';
  }

  function cartUpdateUrl() {
    return (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js';
  }

  function fetchConfig() {
    if (window.theme && theme.utils && theme.utils.fetchConfig) {
      return theme.utils.fetchConfig();
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  function propertyEntries(item) {
    const props = item && item.properties;
    if (!props) return [];
    if (Array.isArray(props)) {
      return props
        .map((entry) => (Array.isArray(entry) ? entry : [entry && entry.name, entry && entry.value]))
        .filter((entry) => entry && entry[0] != null);
    }
    return Object.keys(props).map((key) => [key, props[key]]);
  }

  function isChildLine(item) {
    const rel = item && item.parent_relationship;
    if (!rel) return false;
    if (typeof rel === 'object') {
      return Boolean(rel.parent || rel.parent_key || rel.parent_id);
    }
    return true;
  }

  function isNativeOptionKey(key) {
    return String(key || '').indexOf('(') !== -1;
  }

  function isRelevantProperty(key, value) {
    const name = String(key || '');
    const text = String(value || '').trim();
    if (!name || !text) return false;
    if (name.charAt(0) === '_') return false;
    if (isNativeOptionKey(name)) return false;
    return name.indexOf('צבע') !== -1 || name.indexOf('הטבעת') !== -1 || text === 'כלול';
  }

  function itemTitle(item) {
    return String(item.product_title || item.title || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function formatItem(item) {
    const lines = [];
    propertyEntries(item).forEach(([key, value]) => {
      if (!isRelevantProperty(key, value)) return;
      lines.push(String(key) + ': ' + String(value).trim());
    });
    if (!lines.length) return '';

    let heading = itemTitle(item);
    const quantity = parseInt(item.quantity, 10) || 1;
    if (quantity > 1) heading += ' ×' + quantity;
    return (heading ? heading + '\n' : '') + lines.join('\n');
  }

  function isOrderDetailKey(key) {
    return String(key || '').indexOf(ATTR_PREFIX) === 0;
  }

  function splitValue(text) {
    const value = String(text || '').trim();
    if (!value) return [];
    if (value.length <= MAX_ATTR_LENGTH) return [value];

    const parts = [];
    let remaining = value;
    while (remaining.length > MAX_ATTR_LENGTH) {
      let cut = remaining.lastIndexOf('\n', MAX_ATTR_LENGTH);
      if (cut < Math.floor(MAX_ATTR_LENGTH / 2)) cut = MAX_ATTR_LENGTH;
      parts.push(remaining.slice(0, cut).trim());
      remaining = remaining.slice(cut).trim();
    }
    if (remaining) parts.push(remaining);
    return parts;
  }

  function detailKey(index, partIndex) {
    const suffix = CONTINUATION[partIndex] || String(partIndex + 1);
    return ATTR_PREFIX + index + suffix;
  }

  function buildAttributeUpdate(cart) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const blocks = items.filter((item) => !isChildLine(item)).map(formatItem).filter(Boolean);
    const update = {};
    const existing = (cart && cart.attributes) || {};

    Object.keys(existing).forEach((key) => {
      if (isOrderDetailKey(key)) update[key] = '';
    });

    let itemNumber = 0;
    blocks.forEach((block) => {
      itemNumber += 1;
      splitValue(block).forEach((part, partIndex) => {
        if (partIndex >= CONTINUATION.length) return;
        update[detailKey(itemNumber, partIndex)] = part;
      });
    });

    return update;
  }

  function sameAttributes(next, current) {
    const currentAttrs = current || {};
    const keys = new Set([
      ...Object.keys(next),
      ...Object.keys(currentAttrs).filter(isOrderDetailKey),
    ]);
    for (const key of keys) {
      if (String(next[key] || '') !== String(currentAttrs[key] || '')) return false;
    }
    return true;
  }

  async function fetchCart() {
    const response = await fetch(cartJsUrl(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  async function postAttributes(attributes) {
    const response = await fetch(cartUpdateUrl(), {
      ...fetchConfig(),
      body: JSON.stringify({ attributes }),
    });
    return response.json();
  }

  async function syncCartAttributes(cart) {
    if (cart && Array.isArray(cart.items)) queuedCart = cart;
    else queuedCart = queuedCart || true;
    if (syncing) return pending;

    syncing = true;
    pending = (async () => {
      try {
        let current = null;
        while (true) {
          const requested = queuedCart;
          queuedCart = null;
          if (requested && requested !== true && Array.isArray(requested.items)) {
            current = requested;
          } else {
            current = await fetchCart();
          }
          const update = buildAttributeUpdate(current);
          if (sameAttributes(update, current.attributes)) {
            if (!queuedCart) break;
            continue;
          }
          current = await postAttributes(update);
          if (!queuedCart) break;
        }
      } catch (error) {
        console.warn('Bundle order attributes could not sync', error);
      } finally {
        syncing = false;
      }
    })();

    return pending;
  }

  function onCartUpdate(event) {
    if (!event || event.source === SOURCE) return;
    syncCartAttributes(event.cart);
  }

  function isCheckoutControl(node) {
    if (!(node instanceof Element)) return false;
    const el = node.closest('button, a, input');
    if (!el) return false;
    if (el.getAttribute('name') === 'checkout') return true;
    const href = el.getAttribute('href') || '';
    return /\/checkout(\?|$|#)/.test(href);
  }

  let releasingCheckout = false;
  document.addEventListener(
    'click',
    (event) => {
      if (releasingCheckout || !syncing) return;
      const el = event.target instanceof Element ? event.target.closest('button, a, input') : null;
      if (!isCheckoutControl(el)) return;
      event.preventDefault();
      event.stopPropagation();
      pending.finally(() => {
        releasingCheckout = true;
        try {
          if (el instanceof HTMLAnchorElement && el.href) {
            window.location.href = el.href;
          } else if (el instanceof HTMLElement) {
            el.click();
          }
        } finally {
          releasingCheckout = false;
        }
      });
    },
    true
  );

  function start() {
    if (window.theme && theme.pubsub && theme.pubsub.subscribe) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }
    document.addEventListener('cart:updated', (event) => {
      onCartUpdate({ cart: event.detail && event.detail.cart });
    });
    syncCartAttributes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
